import api from './api';
import { resolveFifaKitImageUrl } from './fifaKit';

export const resolveVipRewardImageUrl = resolveFifaKitImageUrl;

const VIP_STATUS_CACHE_VERSION = 1;
const VIP_STATUS_MEMORY_TTL_MS = 30 * 1000;
const VIP_STATUS_SUMMARY_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const vipStatusMemory = new Map();
const vipStatusRequests = new Map();

const cleanScopePart = (value) => String(value || '').trim().toLowerCase();

export const vipRewardsCacheScopeForUser = (user = {}) => ({
  tenantId: user?.tenant_id || 'main',
  userId: user?.id,
});

const vipStatusScopeKey = ({ tenantId, userId } = {}) => {
  const tenant = cleanScopePart(tenantId);
  const user = cleanScopePart(userId);
  return tenant && user ? JSON.stringify([tenant, user]) : '';
};

const vipStatusStorageKey = (scopeKey) => (
  `vl_vip_rewards_status_summary:v${VIP_STATUS_CACHE_VERSION}:${encodeURIComponent(scopeKey)}`
);

const statusSummary = (status, cachedAt = Date.now()) => ({
  enabled: status?.enabled === true,
  claimable_count: Math.max(0, Number(status?.claimable_count || 0)),
  cached_at: cachedAt,
});

const cacheVipRewardsStatus = (status, scope = {}) => {
  const scopeKey = vipStatusScopeKey(scope);
  if (!scopeKey || !status || typeof status !== 'object') return status;

  const expectedTenant = cleanScopePart(scope.tenantId);
  const responseTenant = cleanScopePart(status?.item?.tenant_id || 'main');
  if (status?.item && responseTenant !== expectedTenant) {
    throw new Error('VIP Rewards status tenant scope mismatch');
  }

  const cachedAt = Date.now();
  vipStatusMemory.set(scopeKey, { status, cachedAt });
  try {
    window.sessionStorage.setItem(
      vipStatusStorageKey(scopeKey),
      JSON.stringify({
        version: VIP_STATUS_CACHE_VERSION,
        scope: scopeKey,
        ...statusSummary(status, cachedAt),
      }),
    );
  } catch {
    // Private browsing and embedded WebViews can deny storage access.
  }
  return status;
};

export const readCachedVipRewardsSummary = (scope = {}) => {
  const scopeKey = vipStatusScopeKey(scope);
  if (!scopeKey) return null;

  const memoryEntry = vipStatusMemory.get(scopeKey);
  if (memoryEntry) return statusSummary(memoryEntry.status, memoryEntry.cachedAt);

  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(vipStatusStorageKey(scopeKey)) || 'null');
    const cachedAt = Number(parsed?.cached_at || 0);
    if (
      parsed?.version !== VIP_STATUS_CACHE_VERSION
      || parsed?.scope !== scopeKey
      || !cachedAt
      || Date.now() - cachedAt > VIP_STATUS_SUMMARY_MAX_AGE_MS
    ) {
      return null;
    }
    return statusSummary(parsed, cachedAt);
  } catch {
    return null;
  }
};

const isLocalDemoVipPreview = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return ['127.0.0.1', 'localhost', '::1'].includes(host) && /^\/demo(\/|$)/.test(window.location.pathname || '');
};

const demoClaimTemplates = [
  {
    id: 'demo-vip-freeplay',
    type: 'freeplay_offer',
    title: 'Freeplay Load Bonus',
    reward_fp: 5,
    reward_cash: 0,
  },
  {
    id: 'demo-vip-cashback',
    type: 'cashback',
    title: 'Wallet Cashback',
    reward_fp: 0,
    reward_cash: 0.75,
  },
];

const readDemoClaimedIds = () => {
  try {
    return new Set(JSON.parse(window.sessionStorage.getItem('demo_vip_claimed_ids') || '[]'));
  } catch {
    return new Set();
  }
};

const demoVipStatus = (claimedIds = readDemoClaimedIds()) => {
  const claims = demoClaimTemplates.map((claim) => ({
    ...claim,
    status: claimedIds.has(claim.id) ? 'claimed' : 'available',
  }));
  const available = claims.filter((claim) => claim.status === 'available');
  return {
    ok: true,
    enabled: true,
    item: {
      enabled: true,
      title: 'VIP Rewards',
      subtitle: 'Freeplay offers and cashback claims',
      banner_image_url: '',
      fallback_banner_url: '/assets/fifa-kit-home.png',
      freeplay_offer: {
        enabled: true,
        cooldown_hours: 24,
        tiers: [
          { min: 5, max: 9.99, reward_fp: 3 },
          { min: 10, max: 19.99, reward_fp: 4 },
          { min: 20, max: null, reward_fp: 5 },
        ],
      },
      cashback: {
        enabled: true,
        cooldown_hours: 24,
        required_deposits: 6,
        cashback_percent: 5,
      },
    },
    claims,
    available_claims: available,
    claimable_count: available.length,
    cashback_progress: {
      deposit_count: 5,
      deposit_total: 15,
    },
  };
};

export const fetchVipRewardsStatus = async (options = {}) => {
  if (isLocalDemoVipPreview()) {
    return demoVipStatus();
  }

  const scopeKey = vipStatusScopeKey(options);
  if (scopeKey && vipStatusRequests.has(scopeKey)) {
    return vipStatusRequests.get(scopeKey);
  }
  const memoryEntry = scopeKey ? vipStatusMemory.get(scopeKey) : null;
  if (
    options.preferCache === true
    && memoryEntry
    && Date.now() - memoryEntry.cachedAt <= VIP_STATUS_MEMORY_TTL_MS
  ) {
    return memoryEntry.status;
  }

  const request = api.get('/api/vip-rewards/status', {
    params: { _ts: Date.now() },
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  }).then((res) => cacheVipRewardsStatus(res.data || {}, options));

  if (!scopeKey) return request;
  vipStatusRequests.set(scopeKey, request);
  try {
    return await request;
  } finally {
    if (vipStatusRequests.get(scopeKey) === request) {
      vipStatusRequests.delete(scopeKey);
    }
  }
};

export const claimVipReward = async (claimId, options = {}) => {
  if (isLocalDemoVipPreview()) {
    const claimedIds = readDemoClaimedIds();
    claimedIds.add(claimId);
    window.sessionStorage.setItem('demo_vip_claimed_ids', JSON.stringify([...claimedIds]));
    const claim = demoClaimTemplates.find((item) => item.id === claimId) || demoClaimTemplates[0];
    const rewardLabel = claim.type === 'cashback'
      ? `$${Number(claim.reward_cash || 0).toFixed(2)} wallet cashback`
      : `${Number(claim.reward_fp || 0)} FP`;
    return {
      ok: true,
      claim_id: claimId,
      reward_fp: claim.reward_fp || 0,
      reward_cash: claim.reward_cash || 0,
      reward_label: rewardLabel,
      status: demoVipStatus(claimedIds),
    };
  }
  const res = await api.post(`/api/vip-rewards/claims/${claimId}/claim`);
  const data = res.data || {};
  if (data.status) cacheVipRewardsStatus(data.status, options);
  return data;
};

export const fetchVipRewardsHistory = async ({ before, limit = 20 } = {}) => {
  const res = await api.get('/api/vip-rewards/history', {
    params: { limit, ...(before ? { before } : {}) },
    headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
  });
  return res.data || { items: [], next_cursor: null };
};

export const fetchAdminVipRewardsConfig = async (tenantId = '') => {
  const params = tenantId ? { tenant_id: tenantId, _ts: Date.now() } : { _ts: Date.now() };
  const res = await api.get('/api/admin/vip-rewards/config', { params });
  return res.data || {};
};

export const updateAdminVipRewardsConfig = async (payload) => {
  const res = await api.put('/api/admin/vip-rewards/config', payload);
  return res.data?.item || null;
};

export const uploadAdminVipRewardsImage = async (slot, file, tenantId = '') => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await api.post(`/api/admin/vip-rewards/image/${slot}`, fd, {
    params: tenantId ? { tenant_id: tenantId } : {},
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data?.item || null;
};

export const clearAdminVipRewardsImage = async (slot, tenantId = '') => {
  const res = await api.delete(`/api/admin/vip-rewards/image/${slot}`, {
    params: tenantId ? { tenant_id: tenantId } : {},
  });
  return res.data?.item || null;
};
